//AdminDashboard.tsx update

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiNavigation } from "react-icons/fi";
import { DashboardFilters } from "Components/Admin/AdminDashboard/DashboardFilters";
import { MetricsCards } from "Components/Admin/AdminDashboard/MetricsCards";
import { OrderStatusBars } from "Components/Admin/AdminDashboard/OrderStatusBars";
import { InventoryStock } from "Components/Admin/AdminDashboard/InventoryStock";
import { CustomerInsights } from "Components/Admin/AdminDashboard/CustomerInsights";
import { OrdersOverTimeChart } from "Components/Admin/AdminDashboard/OrdersOverTimeChart";
import { ChannelPerformance } from "Components/Admin/AdminDashboard/ChannelPerformance";
import { SalesByRegion } from "Components/Admin/AdminDashboard/SalesByRegion";
import { OrderDetailsModal } from "Components/Admin/AdminDashboard/OrderDetailsModal";
import { WarehouseStock } from "Components/Admin/AdminDashboard/WarehouseStock";
import { SalesOverview } from "Components/Admin/AdminDashboard/SalesOverview";
import { useGetTodayOrderStatusSummaryQuery } from "Services/Admin/DashboardApiSlice";

// Helper to format dates
// Helper to format dates to dd/MM/yyyy as required by API
const fmt = (d: Date) => {
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const plant = process.env.REACT_APP_PLANT;
  const { data: summaryData, isLoading: isSummaryLoading, isFetching: isSummaryFetching } = useGetTodayOrderStatusSummaryQuery(
    { plant: plant || "" },
    { pollingInterval: 60000, refetchOnMountOrArgChange: true }
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");

  // Global Date State (Default to Today)
  const [globalDate, setGlobalDate] = useState({
    range: "Today",
    from: fmt(new Date()),
    to: fmt(new Date()),
    mockMultiplier: 0.05 // Matches the "Today" multiplier
  });

  // Additional Filter States
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [selectedChannel, setSelectedChannel] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");

  // Calculate Dates & Mock Multiplier
  const handleDateChange = (range: string, customFrom?: string, customTo?: string) => {
    const today = new Date();
    let from = new Date();
    let to = new Date();
    let mult = 1;

    // Helper to parse both DD/MM/YYYY and YYYY-MM-DD
    const parse = (str: string) => {
      if (!str) return new Date();
      if (str.includes('/')) {
        const [d, m, y] = str.split('/');
        return new Date(`${y}-${m}-${d}`);
      }
      return new Date(str);
    };

    switch (range) {
      case "Today": mult = 0.05; break;
      case "Yesterday": from.setDate(today.getDate()-1); to.setDate(today.getDate()-1); mult = 0.06; break;
      case "Last 7 Days": from.setDate(today.getDate()-7); mult = 0.25; break;
      case "This Week": from.setDate(today.getDate() - today.getDay()); mult = 0.15; break;
      case "Last 15 Days": from.setDate(today.getDate()-15); mult = 0.5; break;
      case "Last 30 Days": from.setDate(today.getDate()-30); mult = 1; break;
      case "This Month": from.setMonth(today.getMonth(), 1); mult = 0.6; break;
      case "Last Month": from.setMonth(today.getMonth()-1, 1); to.setDate(0); mult = 1.1; break;
      case "This Quarter": from.setMonth(Math.floor(today.getMonth() / 3) * 3, 1); mult = 2.5; break;
      case "Last Quarter": from.setMonth(Math.floor(today.getMonth() / 3) * 3 - 3, 1); to.setMonth(from.getMonth() + 3, 0); mult = 2.8; break;
      case "This Year": from.setMonth(0, 1); mult = 5; break;
      case "Last Year": from.setFullYear(today.getFullYear()-1, 0, 1); to.setFullYear(today.getFullYear()-1, 11, 31); mult = 12; break;
      case "Custom Range":
        from = parse(customFrom || globalDate.from);
        to = parse(customTo || globalDate.to);
        const days = Math.max(1, (to.getTime() - from.getTime()) / (1000 * 3600 * 24));
        mult = days / 30;
        break;
    }

    setGlobalDate({ range, from: fmt(from), to: fmt(to), mockMultiplier: mult });
  };

  // Safe Math helper to apply the mock multiplier while APIs are pending
  const applyMock = (val: number) => Math.max(1, Math.round(val * globalDate.mockMultiplier));
  
  // Safe count formatter to prevent NaN
  const formatCount = (val: any, loading: boolean) => {
    if (loading) return "...";
    return (Number(val) || 0).toLocaleString();
  };

  const pendingCount = summaryData?.results?.pendingCount ?? summaryData?.pendingCount;
  const shippedCount = summaryData?.results?.shippedCount ?? summaryData?.shippedCount;
  const deliveredCount = summaryData?.results?.deliveredCount ?? summaryData?.deliveredCount;

  const handleScrollToStatus = (statusId: string) => {
    const element = document.getElementById(`status-${statusId}`);
    if (element) element.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleCardClick = (status: string) => {
    setSelectedStatus(status);
    setIsModalOpen(true);
  };

  return (
    <div className="bg-[#F4F7FE] min-h-screen p-4 md:p-6 font-sans">
      
      {/* NAVIGATION TABS */}
      <div className="flex items-center gap-2 mb-6">
        <button 
          className="px-5 py-2 text-xs font-bold rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] text-white shadow-md border border-transparent flex items-center gap-1.5 transition-all"
        >
          📊 Overview
        </button>
        <button 
          onClick={() => navigate("/admin/analytics")}
          className="px-5 py-2 text-xs font-bold rounded-full bg-white text-[#1B2559] hover:bg-gray-50 border border-gray-200 shadow-sm flex items-center gap-1.5 hover:shadow-md transition-all"
        >
          📈 Search Analytics
        </button>
      </div>

      {/* GLOBAL FILTERS */}
      <DashboardFilters 
        globalDate={globalDate} 
        onDateChange={handleDateChange} 
        selectedWarehouse={selectedWarehouse}
        onWarehouseChange={setSelectedWarehouse}
        selectedChannel={selectedChannel}
        onChannelChange={setSelectedChannel}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        selectedProduct={selectedProduct}
        onProductChange={setSelectedProduct}
      />

      {/* DASHBOARD CONTENT CONTAIMENT - Prevents endless expansion on Ultra-wide screens */}
      <div className="max-w-[1600px] mx-auto w-full flex flex-col">
        {/* TOP ORDER SUMMARY */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
          <div onClick={() => handleCardClick('Pending')} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all flex items-center gap-3 group">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-tight leading-tight">New Orders (Pending)</p>
                <span className="text-[8px] font-bold bg-gray-50 text-gray-500 px-1 py-0.5 rounded border border-gray-200 shrink-0">TODAY</span>
              </div>
              <h3 className="text-xl font-bold text-[#1B2559] mt-1 group-hover:text-amber-500 transition-colors">
                {formatCount(pendingCount, isSummaryLoading || isSummaryFetching)}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-500 text-lg shrink-0">⏳</div>
          </div>
          <div onClick={() => handleCardClick('Shipped')} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all flex items-center gap-3 group">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-tight leading-tight">Shipped Orders</p>
                <span className="text-[8px] font-bold bg-gray-50 text-gray-500 px-1 py-0.5 rounded border border-gray-200 shrink-0">TODAY</span>
              </div>
              <h3 className="text-xl font-bold text-[#1B2559] mt-1 group-hover:text-blue-500 transition-colors">
                {formatCount(shippedCount, isSummaryLoading || isSummaryFetching)}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-500 text-lg shrink-0">📦</div>
          </div>
          <div onClick={() => handleCardClick('Delivered')} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all flex items-center gap-3 group">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-tight leading-tight">Delivered Orders</p>
                <span className="text-[8px] font-bold bg-gray-50 text-gray-500 px-1 py-0.5 rounded border border-gray-200 shrink-0">TODAY</span>
              </div>
              <h3 className="text-xl font-bold text-[#1B2559] mt-1 group-hover:text-emerald-500 transition-colors">
                {formatCount(deliveredCount, isSummaryLoading || isSummaryFetching)}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 text-lg shrink-0">✅</div>
          </div>
        </div>

        <MetricsCards 
          fromDate={globalDate.from} 
          toDate={globalDate.to}
          warehouse={selectedWarehouse}
          categoryId={selectedCategory}
          productId={selectedProduct}
          mockMultiplier={globalDate.mockMultiplier} 
        />

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_440px] 2xl:grid-cols-[1fr_500px] gap-6 items-stretch">
          
          {/* LEFT COLUMN - Balanced composition */}
          <div className="flex flex-col gap-6 min-w-0">
            <SalesOverview 
              fromDate={globalDate.from} 
              toDate={globalDate.to} 
              warehouse={selectedWarehouse} 
              categoryId={selectedCategory} 
              productId={selectedProduct} 
            />

            {/* Pass multiplier to dynamically update Order Status bars */}
            <OrderStatusBars mockMultiplier={globalDate.mockMultiplier} />
          </div>

          {/* RIGHT COLUMN - Protected fixed-width for stable widget rendering */}
          <div className="flex flex-col gap-6 min-w-0">
            <ChannelPerformance 
              fromDate={globalDate.from}
              toDate={globalDate.to}
              warehouse={selectedWarehouse}
              categoryId={selectedCategory}
              productId={selectedProduct}
              mockMultiplier={globalDate.mockMultiplier} 
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <InventoryStock 
                warehouse={selectedWarehouse}
                categoryId={selectedCategory}
                productId={selectedProduct}
                mockMultiplier={globalDate.mockMultiplier} 
              />
              <CustomerInsights mockMultiplier={globalDate.mockMultiplier} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <WarehouseStock 
                warehouse={selectedWarehouse} 
                categoryId={selectedCategory} 
                productId={selectedProduct} 
              />
              <SalesByRegion 
                fromDate={globalDate.from}
                toDate={globalDate.to}
                warehouse={selectedWarehouse}
                categoryId={selectedCategory}
                productId={selectedProduct}
                mockMultiplier={globalDate.mockMultiplier} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      <OrderDetailsModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        orderStatus={selectedStatus} 
      />
    </div>
  );
};
export default AdminDashboard;